import { Trans } from "@lingui/macro";
import { useSnackbar } from "notistack";
import { FC, ReactNode, useCallback } from "react";

import { useConfirmComponent } from "@/context/confirm_component";
import { useFailureTranslator } from "@/hooks/use_failure_translator";
import { useMutationGptAgentDelete } from "@/queries";

import { DialogRemoveAgentConfirmation } from "../dialog_remove_agent_confirmation";

type IBtnRemoveAgentCallbackProps = {
  fire: () => void;
  isLoading: boolean;
};

export interface IBtnRemoveAgentProps {
  agentId: number;
  agentIdx: number;
  onDelete: () => void;
  children: (props: IBtnRemoveAgentCallbackProps) => ReactNode;
}

export const BtnRemoveAgent: FC<IBtnRemoveAgentProps> = ({
  children,
  agentIdx,
  agentId,
  onDelete,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { failureTranslationGet } = useFailureTranslator();

  const { open: showConfirmDialog } = useConfirmComponent(
    DialogRemoveAgentConfirmation,
  );

  const { mutateAsync: deleteAgent, isLoading: isAgentDeleting } =
    useMutationGptAgentDelete({
      onSuccess: () => {
        enqueueSnackbar(<Trans>Preset successfully deleted</Trans>, {
          variant: "success",
        });

        onDelete();
      },
      onError: (e) => {
        enqueueSnackbar(failureTranslationGet(e), {
          variant: "error",
        });
      },
    });

  const fire = useCallback(async () => {
    const isConfirm =
      (await showConfirmDialog({
        idx: agentIdx + 1,
      })) === "ok";

    if (isConfirm) {
      await deleteAgent(agentId);
    }
  }, [agentId, agentIdx, deleteAgent, showConfirmDialog]);

  return (
    <>
      {children({
        fire,
        isLoading: isAgentDeleting,
      })}
    </>
  );
};
